/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';
import { useQueryClient } from '@tanstack/react-query';
import { usePushNotifications } from '../hooks/usePushNotifications';

interface User {
  id: number;
  login: string;
  name: string;
  role:
    | 'ADMIN'
    | 'DIRECTOR'
    | 'REGIONAL_MANAGER'
    | 'WAREHOUSE_MANAGER'
    | 'ACCOUNTANT'
    | 'CHIEF_ACCOUNTANT'
    | 'SALES_AGENT'
    | 'FORWARDER'
    | 'CASHIER';
  phone?: string;
  branch_id?: number | null;
  branch_ids?: number[];
  location_id?: number | null;
  photo_url?: string;
  lang?: string;
  theme?: string;
  telegram_id?: string | null;
  telegram_confirmed?: boolean;
  can_view_purchase_prices?: boolean;
  can_edit_delivered_orders?: boolean;
  can_edit_finance_descriptions?: boolean;
  can_edit_finance_operations?: boolean;
  can_view_branch_purchases?: boolean;
  can_manage_purchases?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: { login: string; password: string }) => Promise<User>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Synchronously read from localStorage to prevent initial redirect "flicker"
const getStoredUser = (): User | null => {
  if (typeof window === 'undefined') return null;
  const storedUser = localStorage.getItem('erp_user');
  const token = localStorage.getItem('erp_access_token');
  if (!storedUser || !token) return null;
  try {
    return {
      ...JSON.parse(storedUser),
      can_view_purchase_prices: false,
      can_view_branch_purchases: false,
      can_manage_purchases: false,
      can_edit_delivered_orders: false,
      can_edit_finance_descriptions: false,
      can_edit_finance_operations: false,
    };
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const { subscribe } = usePushNotifications();

  useEffect(() => {
    async function refreshUser() {
      const token = localStorage.getItem('erp_access_token');
      if (token) {
        try {
          // Always fetch fresh user data from DB to pick up any admin changes (e.g. branch reassignment)
          const res = await api.get('/auth/me');
          const freshUser: User = {
            id: res.data.id,
            login: res.data.login,
            name: res.data.name || res.data.login,
            role: (res.data.role || 'ADMIN').toUpperCase() as User['role'],
            phone: res.data.phone || '',
            branch_id: res.data.branch_id || null,
            branch_ids: res.data.branch_ids || [],
            location_id: res.data.location_id || null,
            photo_url: res.data.photo_url || null,
            lang: res.data.lang || 'RU',
            // Тема из БД — нужна только для cross-device sync через SettingsSync.
            // На UI она влияет только если в браузере нет vite-ui-theme (новый браузер/устройство).
            theme: res.data.theme || 'light',
            telegram_id: res.data.telegram_id || null,
            telegram_confirmed: res.data.telegram_confirmed || false,
            can_view_purchase_prices: res.data.can_view_purchase_prices === true,
            can_view_branch_purchases: res.data.can_view_branch_purchases === true,
            can_manage_purchases: res.data.can_manage_purchases === true,
            can_edit_delivered_orders: res.data.can_edit_delivered_orders === true,
            can_edit_finance_descriptions: res.data.can_edit_finance_descriptions === true,
            can_edit_finance_operations: res.data.can_edit_finance_operations === true,
          };
          // Discard a response if the account changed while the request was in flight.
          if (localStorage.getItem('erp_access_token') !== token) return;
          const previous = JSON.parse(localStorage.getItem('erp_user') || '{}');
          if (
            previous.id !== freshUser.id ||
            previous.can_view_purchase_prices !== freshUser.can_view_purchase_prices ||
            previous.can_view_branch_purchases !== freshUser.can_view_branch_purchases ||
            previous.can_manage_purchases !== freshUser.can_manage_purchases ||
            previous.can_edit_delivered_orders !== freshUser.can_edit_delivered_orders ||
            previous.can_edit_finance_descriptions !== freshUser.can_edit_finance_descriptions ||
            previous.can_edit_finance_operations !== freshUser.can_edit_finance_operations ||
            previous.role !== freshUser.role ||
            previous.branch_id !== freshUser.branch_id ||
            JSON.stringify(previous.branch_ids || []) !== JSON.stringify(freshUser.branch_ids)
          ) {
            queryClient.clear();
          }
          localStorage.setItem('erp_user', JSON.stringify(freshUser));
          setUser(freshUser);
        } catch {
          // Token invalid - keep existing user or logout handled by axios interceptor
        }
      }
      setIsLoading(false);
    }

    refreshUser();
    window.addEventListener('focus', refreshUser);
    const permissionRefresh = window.setInterval(refreshUser, 60_000);

    // Listen for global 401 events from axios interceptor
    const handleUnauthorized = () => {
      // Direct cleanup without calling logout (which is declared later)
      localStorage.removeItem('erp_access_token');
      localStorage.removeItem('erp_user');
      queryClient.clear();
      setUser(null);
    };
    window.addEventListener('erp_unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('erp_unauthorized', handleUnauthorized);
      window.removeEventListener('focus', refreshUser);
      window.clearInterval(permissionRefresh);
    };
  }, [queryClient]);

  const login = async (credentials: { login: string; password: string }): Promise<User> => {
    try {
      const response = await api.post('/auth/login', {
        login: credentials.login,
        password: credentials.password,
      });

      const accessToken = response.data.access_token;
      const payloadBase64 = accessToken.split('.')[1];
      // atob() decodes base64 as Latin-1, which corrupts Cyrillic (UTF-8 multibyte) chars.
      // TextDecoder correctly handles UTF-8 bytes.
      const payloadDecoded = JSON.parse(
        new TextDecoder('utf-8').decode(
          Uint8Array.from(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0)),
        ),
      );

      const loggedInUser: User = {
        id: payloadDecoded.sub,
        login: payloadDecoded.login,
        name: payloadDecoded.name || payloadDecoded.login,
        role: (payloadDecoded.role || 'ADMIN').toUpperCase() as User['role'],
        can_view_purchase_prices: response.data.user?.can_view_purchase_prices === true,
        can_view_branch_purchases: response.data.user?.can_view_branch_purchases === true,
        can_manage_purchases: response.data.user?.can_manage_purchases === true,
        can_edit_delivered_orders: response.data.user?.can_edit_delivered_orders === true,
        can_edit_finance_descriptions: response.data.user?.can_edit_finance_descriptions === true,
        can_edit_finance_operations: response.data.user?.can_edit_finance_operations === true,
        phone: payloadDecoded.phone || '',
        branch_id: payloadDecoded.branch_id || null,
        branch_ids: payloadDecoded.branch_ids || [],
        location_id: payloadDecoded.location_id || null,
        photo_url: payloadDecoded.photo_url || null,
        lang: payloadDecoded.lang || 'RU',
        // При первом логине тоже не хардкодим 'light' — берём системную или default
        theme: payloadDecoded.theme || localStorage.getItem('vite-ui-theme') || 'light',
      };

      localStorage.setItem('erp_access_token', accessToken);
      localStorage.setItem('erp_user', JSON.stringify(loggedInUser));
      queryClient.clear();
      setUser(loggedInUser);

      // Attempt to subscribe to push notifications asynchronously
      subscribe().catch(console.error);

      return loggedInUser;
    } catch (error: unknown) {
      const msg = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Login failed:', msg.response?.data?.message || msg.message);
      throw new Error(msg.response?.data?.message || 'Ошибка авторизации. Проверьте логин и пароль.');
    }
  };

  const logout = () => {
    localStorage.removeItem('erp_access_token');
    localStorage.removeItem('erp_user');
    queryClient.clear();
    setUser(null);
  };

  const updateUser = (data: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...data };
      localStorage.setItem('erp_user', JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
